import { computeResizeSync } from '@/lib/boundaries/bounding-box-boundary'
import type { UnifiedSelectionResizeHandle } from '@/lib/transforms/types/unified-selection-resize-handle'

type UnifiedSelectionAspectLock = { current?: 'width' | 'height' }

export function isCornerResizeHandle(direction: UnifiedSelectionResizeHandle): boolean {
  return (
    (direction.includes('n') || direction.includes('s')) &&
    (direction.includes('e') || direction.includes('w'))
  )
}

/**
 * Legacy `page-editor` always scales a photo-filled shape (or one with a photo-filled/rotated
 * child) proportionally on a corner drag — Shift is one of several ways to *trigger* proportional
 * scaling there, never a way to break it (`ODSprite.resize`, odeditorsvg.js, all 4 corner
 * branches: `hasImage() || hasImageChildOrRotatedChild() || isporportional || shiftKey`). Checked
 * before the Shift escape hatch below so Shift+corner on a photo shape stays aspect-locked,
 * matching legacy exactly, instead of breaking aspect lock the way it does for a plain shape.
 */
function shouldUseAspectRatio(
  isCornerHandle: boolean,
  shiftKey: boolean,
  panelDimensionsLinked: boolean,
  hasPhotoFill: boolean
): boolean {
  if (isCornerHandle && hasPhotoFill) return true
  if (shiftKey) return false
  if (isCornerHandle) return true
  return panelDimensionsLinked
}

export function computeUnifiedSelectionOrientedResizeLocal(params: {
  direction: UnifiedSelectionResizeHandle
  initial: { x: number; y: number; width: number; height: number }
  dx: number
  dy: number
  shiftKey: boolean
  panelDimensionsLinked: boolean
  minDimension: number
  aspectLock: UnifiedSelectionAspectLock
  /** See `shouldUseAspectRatio` — forces aspect lock on a corner drag regardless of Shift. */
  hasPhotoFill?: boolean
}): { next: { x: number; y: number; width: number; height: number }; useAspectRatio: boolean } {
  const {
    direction,
    initial,
    dx,
    dy,
    shiftKey,
    panelDimensionsLinked,
    minDimension,
    aspectLock,
    hasPhotoFill = false,
  } = params
  const w0 = initial.width
  const h0 = initial.height
  const isCornerHandle = isCornerResizeHandle(direction)
  const useAspectRatio = shouldUseAspectRatio(
    isCornerHandle,
    shiftKey,
    panelDimensionsLinked,
    hasPhotoFill
  )
  const next = useAspectRatio
    ? (() => {
        if (isCornerHandle) {
          const deltaW = direction.includes('e') ? dx : direction.includes('w') ? -dx : 0
          const deltaH = direction.includes('s') ? dy : direction.includes('n') ? -dy : 0
          const scaleDenomW = Math.max(minDimension, w0)
          const scaleDenomH = Math.max(minDimension, h0)
          const relW = Math.abs(deltaW) / scaleDenomW
          const relH = Math.abs(deltaH) / scaleDenomH
          const lockMovement = Math.hypot(Math.abs(deltaW), Math.abs(deltaH))
          const lockThreshold = Math.min(scaleDenomW, scaleDenomH) * 0.02

          if (!aspectLock.current && lockMovement > lockThreshold) {
            aspectLock.current = relW >= relH ? 'width' : 'height'
          }

          return computeResizeSync(
            {
              direction,
              initial,
              dx,
              dy,
              minDimension,
            },
            { constrainAspect: true, axis: aspectLock.current }
          )
        }

        const edgeAxis = direction === 'n' || direction === 's' ? 'height' : 'width'

        return computeResizeSync(
          {
            direction,
            initial,
            dx,
            dy,
            minDimension,
          },
          { constrainAspect: true, axis: edgeAxis }
        )
      })()
    : computeResizeSync({
        direction,
        initial,
        dx,
        dy,
        minDimension,
      })

  return { next, useAspectRatio }
}
